import { useState } from 'react';
import { contactoService, type CreateContactoMensajeDto } from '../services/contacto/contacto.service';
import { NotificationToast } from '../components/ui/NotificationToast';
import { Send, Loader2 } from 'lucide-react';
import { usePublicContent } from '../hooks/usePublicContent';

export const ContactoPage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<CreateContactoMensajeDto>({
    nombreCompleto: '',
    email: '',
    telefono: '',
    asunto: '',
    mensaje: '',
  });
  const [errors, setErrors] = useState<Partial<Record<keyof CreateContactoMensajeDto, string>>>({});
  const { getText } = usePublicContent('CONTACTO');
  const pageLabel = getText('header', 'label', 'Escríbenos');
  const pageTitle = getText('header', 'titulo', 'Contáctanos');
  const pageSubtitle = getText('header', 'subtitulo', 'Estamos aquí para servirte y acompañarte en tu caminar con Dios');
  const infoLabel = getText('info', 'label', 'Estamos cerca');
  const infoTitle = getText('info', 'titulo', 'Información de Contacto');
  const addressTitle = getText('info', 'direccion_titulo', 'Dirección');
  const address = getText('info', 'direccion', '[Dirección completa de la iglesia]\nApartadó, Antioquia, Colombia');
  const phonesTitle = getText('info', 'telefonos_titulo', 'Teléfonos');
  const phones = getText('info', 'telefonos', 'Principal: [Número principal]\nPastor: [Número del pastor]\nWhatsApp: [Número de WhatsApp]');
  const emailTitle = getText('info', 'email_titulo', 'Email');
  const email = getText('info', 'email', 'General: info@iccuadrangular.com\nPastor: pastor@iccuadrangular.com\nEventos: eventos@iccuadrangular.com');
  const hoursTitle = getText('info', 'horarios_titulo', 'Horarios de Oficina');
  const hours = getText('info', 'horarios', 'Lunes a Viernes: 8:00 AM - 5:00 PM\nSábados: 8:00 AM - 12:00 PM\nDomingos: Solo servicios religiosos');
  const formTitle = getText('form', 'titulo', 'Envíanos un Mensaje');
  const mapLabel = getText('mapa', 'label', 'Encuéntranos');
  const mapTitle = getText('mapa', 'titulo', 'Nuestra Ubicación');
  const mapDescription = getText('mapa', 'descripcion', 'Ven y visítanos, estamos ubicados en el corazón de Apartadó');
  const mapButton = getText('mapa', 'boton', 'Ver en Google Maps');

  const [toast, setToast] = useState<{ isOpen: boolean; title: string; message: string; type: 'success' | 'error' }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'success',
  });

  const showToast = (title: string, message: string, type: 'success' | 'error') => {
    setToast({ isOpen: true, title, message, type });
  };

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof CreateContactoMensajeDto, string>> = {};

    if (!formData.nombreCompleto || formData.nombreCompleto.length < 2) {
      newErrors.nombreCompleto = 'El nombre debe tener al menos 2 caracteres';
    }

    if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Ingresa un correo electrónico válido';
    }

    if (!formData.asunto) {
      newErrors.asunto = 'Selecciona un asunto';
    }

    if (!formData.mensaje || formData.mensaje.length < 10) {
      newErrors.mensaje = 'El mensaje debe tener al menos 10 caracteres';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    setIsSubmitting(true);
    try {
      await contactoService.enviarMensaje(formData);
      showToast('¡Mensaje enviado!', 'Gracias por contactarnos. Te responderemos pronto.', 'success');
      setFormData({
        nombreCompleto: '',
        email: '',
        telefono: '',
        asunto: '',
        mensaje: '',
      });
      setErrors({});
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Error al enviar el mensaje';
      showToast('Error', message, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: keyof CreateContactoMensajeDto, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  return (
    <div className="min-h-screen transition-colors duration-300 bg-stone-100">
      
      {/* Header */}
      <section className="relative overflow-hidden pt-32 pb-20 px-6">
        <div className="absolute inset-0 bg-stone-200/30"></div>
        <div className="relative max-w-7xl mx-auto text-center">
          <p className="text-stone-500 text-sm uppercase tracking-[0.3em] mb-3 font-medium animate-fade-in">{pageLabel}</p>
          <h1 className="text-5xl md:text-6xl font-serif font-semibold mb-2 animate-fade-in text-stone-800">
            {pageTitle}
          </h1>
          <div className="w-24 h-1 bg-gradient-to-r from-purple-600 to-red-500 mx-auto mb-6 rounded-full animate-slide-up delay-100"></div>
          <p className="text-xl text-stone-600 animate-slide-up delay-200">
            {pageSubtitle}
          </p>
        </div>
      </section>

      {/* Contact Info */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16">
            
            {/* Contact Information */}
            <div className="animate-slide-left">
              <p className="text-stone-500 text-sm uppercase tracking-[0.3em] mb-3 font-medium">{infoLabel}</p>
              <h2 className="text-4xl font-serif font-semibold mb-2 text-stone-800">
                {infoTitle}
              </h2>
              <div className="w-20 h-1 bg-gradient-to-r from-purple-600 to-red-500 mb-8 rounded-full"></div>
              
              <div className="space-y-8">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-stone-100 rounded-full flex items-center justify-center shrink-0">
                    <span className="text-stone-600 text-lg">📍</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-serif font-semibold text-stone-800 mb-1">{addressTitle}</h3>
                    <div className="w-6 h-px bg-stone-300 mb-2"></div>
                    <p className="text-stone-600 leading-relaxed whitespace-pre-line">
                      {address}
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-stone-100 rounded-full flex items-center justify-center shrink-0">
                    <span className="text-stone-600 text-lg">📞</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-serif font-semibold text-stone-800 mb-1">{phonesTitle}</h3>
                    <div className="w-6 h-px bg-stone-300 mb-2"></div>
                    <p className="text-stone-600 leading-relaxed whitespace-pre-line">
                      {phones}
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-stone-100 rounded-full flex items-center justify-center shrink-0">
                    <span className="text-stone-600 text-lg">✉️</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-serif font-semibold text-stone-800 mb-1">{emailTitle}</h3>
                    <div className="w-6 h-px bg-stone-300 mb-2"></div>
                    <p className="text-stone-600 leading-relaxed whitespace-pre-line">
                      {email}
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-stone-100 rounded-full flex items-center justify-center shrink-0">
                    <span className="text-stone-600 text-lg">🕐</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-serif font-semibold text-stone-800 mb-1">{hoursTitle}</h3>
                    <div className="w-6 h-px bg-stone-300 mb-2"></div>
                    <p className="text-stone-600 leading-relaxed whitespace-pre-line">
                      {hours}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="animate-slide-right">
              <div className="bg-white rounded-2xl p-8 shadow-sm border border-stone-200/60">
                <h2 className="text-2xl font-serif font-semibold text-stone-800 mb-2 text-center">
                  {formTitle}
                </h2>
                <div className="w-12 h-px bg-stone-300 mx-auto mb-6"></div>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">
                      Nombre Completo *
                    </label>
                    <input
                      type="text"
                      value={formData.nombreCompleto}
                      onChange={(e) => handleChange('nombreCompleto', e.target.value)}
                      className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-purple-500/20 focus:border-purple-700 transition-all duration-300 text-stone-900 bg-stone-50 ${
                        errors.nombreCompleto ? 'border-red-400' : 'border-stone-300'
                      }`}
                      placeholder="Tu nombre completo"
                    />
                    {errors.nombreCompleto && (
                      <p className="mt-1 text-sm text-red-500">{errors.nombreCompleto}</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-purple-500/20 focus:border-purple-700 transition-all duration-300 text-stone-900 bg-stone-50 ${
                        errors.email ? 'border-red-400' : 'border-stone-300'
                      }`}
                      placeholder="tu@email.com"
                    />
                    {errors.email && (
                      <p className="mt-1 text-sm text-red-500">{errors.email}</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">
                      Teléfono
                    </label>
                    <input
                      type="tel"
                      value={formData.telefono}
                      onChange={(e) => handleChange('telefono', e.target.value)}
                      className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:ring-2 focus:ring-purple-500/20 focus:border-purple-700 transition-all duration-300 text-stone-900 bg-stone-50"
                      placeholder="Tu número de teléfono"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">
                      Asunto *
                    </label>
                    <select
                      value={formData.asunto}
                      onChange={(e) => handleChange('asunto', e.target.value)}
                      className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-purple-500/20 focus:border-purple-700 transition-all duration-300 text-stone-900 bg-stone-50 ${
                        errors.asunto ? 'border-red-400' : 'border-stone-300'
                      }`}
                    >
                      <option value="">Selecciona un asunto</option>
                      <option value="Consulta General">Consulta General</option>
                      <option value="Petición de Oración">Petición de Oración</option>
                      <option value="Visita Pastoral">Visita Pastoral</option>
                      <option value="Eventos y Actividades">Eventos y Actividades</option>
                      <option value="Donaciones">Donaciones</option>
                      <option value="Otros">Otros</option>
                    </select>
                    {errors.asunto && (
                      <p className="mt-1 text-sm text-red-500">{errors.asunto}</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">
                      Mensaje *
                    </label>
                    <textarea
                      rows={4}
                      value={formData.mensaje}
                      onChange={(e) => handleChange('mensaje', e.target.value)}
                      className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-purple-500/20 focus:border-purple-700 transition-all duration-300 text-stone-900 bg-stone-50 resize-none ${
                        errors.mensaje ? 'border-red-400' : 'border-stone-300'
                      }`}
                      placeholder="Escribe tu mensaje aquí..."
                    ></textarea>
                    {errors.mensaje && (
                      <p className="mt-1 text-sm text-red-500">{errors.mensaje}</p>
                    )}
                  </div>
                  
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-purple-800 text-white py-4 px-6 rounded-xl font-semibold hover:bg-purple-700 transition-all duration-300 transform hover:scale-105 disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center space-x-2 shadow-sm"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <span>Enviando...</span>
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        <span>Enviar Mensaje</span>
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-24 px-6 bg-stone-200/30">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-stone-500 text-sm uppercase tracking-[0.3em] mb-3 font-medium">{mapLabel}</p>
          <h2 className="text-4xl md:text-5xl font-serif font-semibold mb-2 text-stone-800">
            {mapTitle}
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-purple-600 to-red-500 mx-auto mb-6 rounded-full"></div>
          <p className="text-stone-600 mb-12">
            {mapDescription}
          </p>
          
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-stone-200/60">
            <div className="h-96 bg-stone-200/40 rounded-xl flex items-center justify-center border border-stone-200/60">
              <div className="text-center">
                <div className="w-24 h-24 bg-stone-300/50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-stone-500 font-semibold text-3xl">📍</span>
                </div>
                <p className="text-stone-500 font-serif italic text-lg">
                  [Aquí iría el mapa de Google Maps integrado]
                </p>
                <div className="w-16 h-px bg-stone-300 mx-auto mt-4 mb-4"></div>
                <button className="bg-purple-800 text-white px-6 py-3 rounded-xl font-semibold hover:bg-purple-700 transition-all duration-300 transform hover:scale-105 shadow-sm">
                  {mapButton}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Toast */}
      <NotificationToast
        isOpen={toast.isOpen}
        title={toast.title}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast(prev => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
};

export default ContactoPage;
